import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useUpdateSettingsProfile } from "@/api/mutations/settingsProfiles";
import { RepoBranchPicker } from "@/components/gitea/RepoBranchPicker";
import type { SettingsProfile } from "@/types";

interface Props {
  profile: SettingsProfile;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UpdateSettingsProfileDialog({ profile, open, onOpenChange }: Props) {
  const [name, setName] = useState(profile.name);
  const [description, setDescription] = useState(profile.description);
  const [seedRepo, setSeedRepo] = useState<string | null>(profile.seedRepo);
  const [seedBranch, setSeedBranch] = useState<string | null>(profile.seedBranch);
  const [nameError, setNameError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const mutation = useUpdateSettingsProfile();

  function handleClose(o: boolean) {
    if (!o) {
      setName(profile.name);
      setDescription(profile.description);
      setSeedRepo(profile.seedRepo);
      setSeedBranch(profile.seedBranch);
      setNameError(null);
      setServerError(null);
    }
    onOpenChange(o);
  }

  function handleSubmit() {
    if (name.trim().length === 0) {
      setNameError("Pflichtfeld");
      return;
    }
    setNameError(null);
    setServerError(null);
    mutation.mutate(
      { id: profile.id, name: name.trim(), description: description.trim(), seedRepo, seedBranch },
      {
        onSuccess: () => {
          toast.success("Profil aktualisiert");
          onOpenChange(false);
        },
        onError: (err) => {
          const msg = (err as Error).message;
          setServerError(msg);
          toast.error(`Update fehlgeschlagen: ${msg}`);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="update-settings-profile-dialog">
        <DialogHeader>
          <DialogTitle>Profil bearbeiten</DialogTitle>
          <DialogDescription>Name + Beschreibung ändern.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="sp-edit-name">Name</Label>
            <Input
              id="sp-edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="sp-edit-field-name"
              autoFocus
            />
            {nameError && <div className="text-xs text-red">{nameError}</div>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="sp-edit-description">Beschreibung</Label>
            <Textarea
              id="sp-edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              data-testid="sp-edit-field-description"
            />
          </div>
          <RepoBranchPicker
            seedRepo={seedRepo}
            seedBranch={seedBranch}
            onChange={(r, b) => {
              setSeedRepo(r);
              setSeedBranch(b);
            }}
          />
        </div>

        {serverError && (
          <div className="text-sm text-red" data-testid="sp-edit-server-error">
            {serverError}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} data-testid="sp-edit-cancel">
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={mutation.isPending} data-testid="sp-edit-submit">
            {mutation.isPending ? "..." : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
